import React from 'react';
import './DefaultSettingsPage.scss'
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import {LocationModal} from "../DealPage/LocationModal";
import Divider from "@mui/material/Divider";
import {useModalsStore} from "../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";
import {buildLocationString} from "../../utils/utils";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import classnames from "classnames";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import {useDefaultQuoteSettingsContext} from "../../contexts/DefaultQuoteSettingsContext";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {DefaultSettingsFeesAndTaxes} from "./DefaultSettingsFeesAndTaxes";
import Button from "@mui/material/Button";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import useEnterEffect from "../../hooks/useEnterEffect";
import {useThemeStore} from "../../stores/themeStore";



// Drop the menu directly below the control, left-aligned — same as the DealPage.
const selectMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    marginThreshold: 4,
}

export const DefaulSettingsPage = () => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const theme = useThemeStore((state) => state.theme)
    const { quoteSettings, setSettingsStateWithUpdate, updateQuoteSettings, updateQuoteSettingsResponse } = useDefaultQuoteSettingsContext()

    const {
        openModal,
        closeModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModalById: state.closeModalById,
        }))
    )

    const saveSettings = () => {
        return updateQuoteSettings(quoteSettings)
    }

    useEnterEffect(function saveSettingsOnEnter(){
        saveSettings()
    }, [quoteSettings])

    const saveLocation = (location, shouldSetCustomerLocation, shouldSetDealerLocation) => {
        let updateObj = {}
        if (shouldSetCustomerLocation) {
            updateObj = {
                customer_location: location,
                customer_location_id: location.id
            }
        }
        if (shouldSetDealerLocation) {
            updateObj = {
                ...updateObj,
                dealer_location: location,
                dealer_location_id: location.id
            }
        }
        if (!_.isEmpty(updateObj)) {
            setSettingsStateWithUpdate(updateObj, true)
        }

        closeModalById("LocationModal")
    }

    if (!quoteSettings) return null

    const customerLocation = quoteSettings?.customer_location
    const dealerLocation = quoteSettings?.dealer_location
    const cashName = quoteSettings.is_drive_off_cap_reduction ? 'Cap Reduction:' : 'Total Drive Off:'
    const toggleTooltipName = quoteSettings.is_drive_off_cap_reduction ? 'Total Drive Off' : 'Cap Reduction'

    const isLoading =  updateQuoteSettingsResponse?.loading

    return (
        <Box id={"DefaulSettingsPage"} data-theme={theme}>
            {/* ── Top bar: blue uppercase title + Save button ── */}
            <div className={'ds-topbar'}>
                <span className={'ds-topbar-title'}>Default Lease Quote Settings</span>
                <button
                    type="button"
                    className={'ds-btn-save'}
                    onClick={saveSettings}
                    disabled={isLoading}
                >
                    {isLoading ? 'SAVING…' : 'SAVE'}
                    {!isLoading && <KeyboardReturnIcon/>}
                </button>
            </div>

            {/* ── Row 1: Vehicle Pricing | Location ── */}
            <div className={'ds-top-grid'}>
                <Box className={'ds-panel panel-pricing'}>
                    <div className={'panel-header'}>
                        <div className={'panel-title'}>Vehicle Pricing</div>
                    </div>
                    <div className={'panel-body'}>
                        {/* Profit */}
                        <div className={'pricing-row'}>
                            <span className={'pricing-label'}>Profit:</span>
                            <div className={'pricing-input-wrap'}>
                                <span className={'currency-prefix'}>$</span>
                                <CustomCurrencyInput
                                    value={quoteSettings.profit}
                                    onChange={(value) => setSettingsStateWithUpdate({profit: value}, false)}
                                    InputProps={{}}
                                />
                            </div>
                        </div>

                        {/* Drive Off / Cap Reduction — label first, then the toggle */}
                        <div className={'toggle-row'}>
                            <span>{cashName}</span>
                            <Tooltip title={`Use cash as ${toggleTooltipName}`} arrow placement={'top'}>
                                <Switch
                                    checked={Boolean(quoteSettings.is_drive_off_cap_reduction)}
                                    onChange={() => setSettingsStateWithUpdate(
                                        { is_drive_off_cap_reduction: !quoteSettings.is_drive_off_cap_reduction }, true)
                                    }
                                />
                            </Tooltip>
                            <div className={'toggle-input-wrap'}>
                                <span className={'currency-prefix'}>$</span>
                                <CustomCurrencyInput
                                    allowNull={true}
                                    value={quoteSettings.drive_off}
                                    onChange={(value) => setSettingsStateWithUpdate({drive_off: value}, false)}
                                    InputProps={{}}
                                />
                            </div>
                        </div>

                        {/* Mileage */}
                        <div className={'select-row'}>
                            <label>Mileage:</label>
                            <Select
                                className={'ds-select-mui'}
                                value={quoteSettings.mileage ?? ''}
                                MenuProps={{ ...selectMenuProps, MenuListProps: { className: 'ds-select-menu' } }}
                                onChange={(e) => setSettingsStateWithUpdate({mileage: e.target.value}, true)}
                            >
                                {[10000, 12000, 15000].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                            </Select>
                        </div>

                        {/* Mileage Ford & Lincoln — only shown when Mileage = 10000 */}
                        {
                            Boolean(quoteSettings.mileage === 10000) &&
                            <div className={'select-row'}>
                                <label>
                                    Mileage Ford &amp; Lincoln:
                                    <Tooltip
                                        title={'Selecting 10500 will limit the banks returned to only those offering 10500. ' +
                                            'We recommend setting to 12000 to return all banks and if needed manually changing ' +
                                            'to desired mileage later on the quote page.'}
                                        arrow placement={'top'}
                                    >
                                        <span className={'ds-info-icon'}>i</span>
                                    </Tooltip>
                                </label>
                                <Select
                                    className={'ds-select-mui'}
                                    value={quoteSettings.mileage_alt ?? ''}
                                    MenuProps={{ ...selectMenuProps, MenuListProps: { className: 'ds-select-menu' } }}
                                    onChange={(e) => setSettingsStateWithUpdate({mileage_alt: e.target.value}, true)}
                                >
                                    {[10500, 12000, 15000].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                </Select>
                            </div>
                        }
                    </div>
                </Box>

                {/* Location panel */}
                <Box className={'ds-panel panel-location'}>
                    <div className={'panel-header'}>
                        <div className={'panel-title'}>Default and Saved Locations</div>
                        <Tooltip title="Edit Locations" arrow placement={'top'}>
                            <button
                                type="button"
                                className={'panel-header-edit'}
                                onClick={() => {
                                    openModal({
                                        element: LocationModal,
                                        id: "LocationModal",
                                        props: {
                                            selectLocation: saveLocation,
                                            isSaving: false,
                                            // Settings manages the saved-location
                                            // list, so it keeps the explicit Add step.
                                            allowManualAdd: true,
                                            // One default in this UI; customer_location
                                            // is the field the rest of the app reads as
                                            // "the" default.
                                            defaultLocation: quoteSettings.customer_location,
                                            title: "Default and Saved Locations",
                                        }
                                    })
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.97 9.304A8 8 0 0 0 2 10c0 4.69 4.887 9.562 7.022 11.468"/>
                                    <path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/>
                                    <circle cx="10" cy="10" r="3"/>
                                </svg>
                            </button>
                        </Tooltip>
                    </div>
                    <div className={'panel-body'}>
                        <div className={'location-row'}>
                            <span className={'location-key'}>Customer:</span>
                            <span className={'location-val'}>{ buildLocationString(customerLocation) }</span>
                        </div>
                        <div className={'location-row'}>
                            <span className={'location-key'}>Dealer:</span>
                            <span className={'location-val'}>{ buildLocationString(dealerLocation) }</span>
                        </div>
                    </div>
                </Box>
            </div>

            <DefaultSettingsFeesAndTaxes/>
        </Box>
    )
}